import { Link, useLocation } from "react-router-dom";
import { useAuth, useTier } from "@/context/AuthContext";

// TrialBanner — slim bronze strip surfaced above every protected page.
// Rendered by Layout.jsx just above the <Outlet />.
//   • Only visible when user.plan === "trial".
//   • Days-left is a ceil() of the delta so a user who signs up at
//     11:59pm still sees "3 days" for the first calendar day.
//   • "Ends today" and "Ended" edge copy is included even though the
//     backend gate should redirect an expired user long before they
//     see this component — it's a defensive UX safety net.
//   • Hidden entirely for admin sessions (`is_admin=true`) or on any
//     route under `/admin` — admins are not billing prospects and the
//     dashboard is dense enough without a promo strip.
export default function TrialBanner() {
  const { user } = useAuth();
  const { isAdmin } = useTier();
  const { pathname } = useLocation();
  if (isAdmin || pathname.startsWith("/admin")) return null;
  // Belt-and-braces: hide once Basic is owned even if `plan` hasn't yet
  // transitioned off "trial" (e.g. stale session before /refresh lands).
  if (!user || user.plan !== "trial" || user.basic_owned) return null;

  const exp = user.trial_expires_at ? new Date(user.trial_expires_at) : null;
  if (!exp || Number.isNaN(exp.getTime())) return null;

  const msLeft = exp.getTime() - Date.now();
  const daysLeft = Math.max(0, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));

  let message;
  if (msLeft <= 0)         message = "Your free trial has ended.";
  else if (daysLeft === 1) message = "Your free trial ends today.";
  else                     message = `Your free trial ends in ${daysLeft} days.`;

  return (
    <div
      data-testid="trial-banner"
      className="w-full px-4 py-2 flex items-center justify-center gap-3 text-xs"
      style={{
        background: "linear-gradient(90deg, #1a1a1a 0%, #2a2116 50%, #1a1a1a 100%)",
        borderBottom: "1px solid #C9A86A",
        color: "#f7f7f7",
        letterSpacing: "0.3px",
      }}
    >
      <span>{message}</span>
      <Link
        to="/auth/purchase-plan"
        data-testid="trial-banner-buy-link"
        className="font-semibold hover:underline"
        style={{ color: "#C9A86A" }}
      >Buy Basic →</Link>
    </div>
  );
}
